import React, { forwardRef } from 'react';

/**
 * Modular Card component implementing the modern Stride visual language.
 */
const Card = forwardRef(({
  children,
  variant = 'default',
  padding = 'none',
  className = '',
  onClick,
  as: Component = 'div',
  ...rest
}, ref) => {
  const isClickable = Boolean(onClick);

  const variantClasses = {
    default:
      'bg-white border border-border rounded-xl shadow-card',
    interactive:
      'bg-white border border-border rounded-xl shadow-card hover:shadow-card-hover hover:border-border-hover hover:-translate-y-0.5 transition-all duration-200 cursor-pointer',
    flat:
      'bg-surface border border-border rounded-xl',
    bordered:
      'bg-white border border-border rounded-xl',
    accent:
      'bg-white border border-border rounded-xl shadow-card relative overflow-hidden before:content-[\'\'] before:absolute before:inset-x-0 before:top-0 before:h-1 before:bg-gradient-to-r before:from-primary before:via-[#7489d4] before:to-[#9aa8dc]',
  };

  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-5 md:p-6',
    lg: 'p-6 md:p-8',
  };

  return (
    <Component
      ref={ref}
      onClick={onClick}
      className={`
        relative transition-all duration-150
        ${variantClasses[variant] || variantClasses.default}
        ${paddingClasses[padding] || ''}
        ${isClickable && variant !== 'interactive' ? 'cursor-pointer hover:shadow-md' : ''}
        ${className}
      `}
      {...rest}
    >
      {children}
    </Component>
  );
});

Card.displayName = 'Card';

/**
 * Card Header
 */
const CardHeader = ({
  children,
  title,
  subtitle,
  action,
  className = '',
  border = true,
  ...rest
}) => {
  return (
    <div
      className={`
        px-5 py-4 md:px-6 md:py-5 flex items-center justify-between gap-4 flex-wrap
        ${border ? 'border-b border-gray-100' : ''}
        ${className}
      `}
      {...rest}
    >
      {title || subtitle ? (
        <div className="min-w-0 flex-1">
          {title && <CardTitle>{title}</CardTitle>}
          {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        </div>
      ) : null}

      {children}

      {action && (
        <div className="shrink-0 flex items-center gap-2">
          {action}
        </div>
      )}
    </div>
  );
};
CardHeader.displayName = 'Card.Header';

/**
 * Card Title
 */
const CardTitle = ({
  children,
  as: Component = 'h3',
  className = '',
  ...rest
}) => {
  return (
    <Component
      className={`text-stride-heading font-bold text-base md:text-lg tracking-tight leading-snug ${className}`}
      {...rest}
    >
      {children}
    </Component>
  );
};
CardTitle.displayName = 'Card.Title';

/**
 * Card Subtitle / Description
 */
const CardSubtitle = ({
  children,
  className = '',
  ...rest
}) => {
  return (
    <p
      className={`text-xs md:text-sm text-gray-500 font-normal mt-1 leading-normal ${className}`}
      {...rest}
    >
      {children}
    </p>
  );
};
CardSubtitle.displayName = 'Card.Subtitle';

/**
 * Card Body / Content
 */
const CardContent = ({
  children,
  className = '',
  padding = 'md',
  ...rest
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-5 md:p-6',
    lg: 'p-6 md:p-8',
  };

  return (
    <div
      className={`${paddingClasses[padding] || paddingClasses.md} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
};
CardContent.displayName = 'Card.Content';

/**
 * Card Footer
 */
const CardFooter = ({
  children,
  className = '',
  border = true,
  ...rest
}) => {
  return (
    <div
      className={`
        px-5 py-3 md:px-6 md:py-4 bg-gray-50/60 flex items-center justify-between flex-wrap gap-3 rounded-b-xl
        ${border ? 'border-t border-gray-100' : ''}
        ${className}
      `}
      {...rest}
    >
      {children}
    </div>
  );
};
CardFooter.displayName = 'Card.Footer';

/**
 * Card Stat (for KPIs, Pipeline stages, Analytics)
 */
const CardStat = ({
  label,
  value,
  caption,
  icon,
  trend, // { value: '+12%', positive: true }
  onClick,
  className = '',
}) => {
  const isClickable = Boolean(onClick);

  return (
    <div
      onClick={onClick}
      className={`
        flex flex-col p-4 rounded-lg bg-white border border-border
        ${isClickable ? 'cursor-pointer hover:border-border-hover hover:bg-surface transition-all' : ''}
        ${className}
      `}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          {label}
        </span>
        {icon && <span className="text-gray-400">{icon}</span>}
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl md:text-3xl font-extrabold text-stride-heading tracking-tight">
          {value}
        </span>
        {trend && (
          <span
            className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
              trend.positive
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-red-50 text-red-600'
            }`}
          >
            {trend.value}
          </span>
        )}
      </div>

      {caption && (
        <span className="text-xs text-gray-400 mt-1">
          {caption}
        </span>
      )}
    </div>
  );
};
CardStat.displayName = 'Card.Stat';

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
Card.Description = CardSubtitle;
Card.Body = CardContent;
Card.Content = CardContent;
Card.Footer = CardFooter;
Card.Stat = CardStat;

export default Card;

